/*
 * MachinePostureCard — "if these values are this, then THIS is how the machine
 * is standing." Draws the machine as an articulated side-view rig and poses it
 * from the selected/live telemetry frame. THREE rigs, picked by the machine's
 * category/model — a scissor lift must look like a scissor lift:
 *
 *   boom     (BT/BA/TB…)   base + turntable, main boom ('2700' angle, '2702'
 *                          extension), jib ('2703'), level cage; load vs
 *                          loadchart ('2724'/'2755').
 *   scissor  (JCPT/1212N…) RT chassis + scissor stack + platform. These
 *                          machines stream NO height signal (K120 decodes
 *                          load_pct / rpm / battery / hours), so the stack is
 *                          drawn mid-rise and the MODEL working height (JCPT
 *                          18xx -> ~18 m) is labelled; live chips carry
 *                          platform load %, engine rpm, battery V, hours.
 *   spider   (XT/LEO…)     crawler tracks + folding twin boom + cage; poses
 *                          from the boom keys when present, else parked.
 *
 * CAN sentinels (-360, -8000, -120, -40…) mean "signal not available" — the
 * part poses neutrally and the chip greys out. Drawn parts, not photo
 * cut-outs: vector parts rotate exactly at their pivots for every model with
 * no per-model image cutting.
 */
import React, { useMemo, useState, useCallback, Suspense } from 'react';
import { Box, Card, Typography, Chip, Stack, Tooltip, ToggleButton, ToggleButtonGroup, CircularProgress } from '@mui/material';
import { PrecisionManufacturing as RigIcon } from '@mui/icons-material';
import { formatToIST } from '../../utils/dataUtils';

// three.js is ~600 KB — only a user who actually opens the 3D view pays for it.
const MachineModel3D = React.lazy(() => import('./MachineModel3D'));

// The 3D machine exists for the Dingli boom family today (the GLB is a
// BA22CRT2); every other rig keeps the vector drawing until its model is built.
export const has3DModel = (rig, model) =>
  rig === 'boom' && /^(BA|BT)\d{2}/.test(String(model || '').toUpperCase());

// Asked once per session: a browser with no WebGL (or a jsdom test run) must
// never even download three.js — it goes straight to the vector rig.
let webglOk = null;
export const supportsWebGL = () => {
  if (webglOk !== null) return webglOk;
  try {
    const c = document.createElement('canvas');
    webglOk = !!(window.WebGLRenderingContext
      && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch {
    webglOk = false;
  }
  return webglOk;
};

const VIEW_KEY = 'machinePostureView';   // '3d' | '2d', remembered per browser
const readView = () => {
  try { return localStorage.getItem(VIEW_KEY) === '2d' ? '2d' : '3d'; } catch { return '3d'; }
};

// ── value extraction ────────────────────────────────────────────────────────
const pickFrom = (src, aliases, { min = -Infinity, max = Infinity } = {}) => {
  if (!src) return null;
  for (const k of aliases) {
    const raw = src[k];
    if (raw == null || raw === '') continue;
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) continue;
    if (n < min || n > max) continue;   // sentinel / out-of-physical-range
    return n;
  }
  return null;
};

export const extractPosture = (frame) => {
  // K120/BA decoders nest their signals under `decoded` — flatten them in.
  const src = frame ? { ...(frame.decoded || {}), ...frame } : null;
  const pick = (aliases, range) => pickFrom(src, aliases, range);
  return {
    boom: pick(['2700', 'main_boom_angle', 'Main Boom Angle/ Main boom to turntable angle'], { min: -15, max: 95 }),
    extend: pick(['2702', 'main_boom_extend_length'], { min: 0, max: 40 }),
    jib: pick(['2703', 'jib_angle'], { min: -75, max: 80 }),
    cageSwing: pick(['2756', 'cage_swing_angle'], { min: -190, max: 370 }),
    tiltX: pick(['2721', 'chassis_tilt_angle_x', 'incline_x_deg'], { min: -25, max: 25 }),
    tiltY: pick(['2723', 'chassis_tilt_angle_y', 'incline_y_deg'], { min: -25, max: 25 }),
    load: pick(['2724', 'cage_load'], { min: 0, max: 5000 }),
    rated: pick(['2755', 'loadchart'], { min: 1, max: 5000 }),
    // scissor-family live signals (K120 / AC / BA decoders)
    loadPct: pick(['load_pct', 'platform_load_pct'], { min: 0, max: 250 }),
    heightM: pick(['platform_height', 'platform_height_m', 'height_m'], { min: 0, max: 60 }),
    rpm: pick(['engine_speed_rpm', '190', 'engine_speed'], { min: 0, max: 6000 }),
    batteryV: pick(['battery_voltage_v', 'system_battery_voltage', '710'], { min: 1, max: 120 }),
    soc: pick(['bms_soc', 'battery_pct', '500', 'soc'], { min: 0, max: 100 }),
    hours: pick(['system_working_hours', '247', 'engine_working_hours'], { min: 0.1, max: 200000 }),
  };
};

// Which rig this machine gets. Category (from equipments) decides; model
// prefixes are the fallback when the category is a generic "MEWP".
export const rigTypeFor = (category, model) => {
  const c = String(category || '').toLowerCase();
  const m = String(model || '').toUpperCase();
  if (c.includes('scissor') || /^(JCPT|SCR|GTJZ)/.test(m) || /^\d{4}N/.test(m)) return 'scissor';
  if (c.includes('spider') || /^(XT|LEO|TEUPEN)/.test(m)) return 'spider';
  if (c.includes('mast') || c.includes('vertical')) return 'scissor';
  return 'boom';
};

// "JCPT1823RTL" -> 18, "1212N" -> 12, "BA16NE" -> 16, "LEO30T" -> 30.
export const modelWorkingHeightM = (model) => {
  const mm = String(model || '').match(/(\d{2})/);
  if (!mm) return null;
  const h = parseInt(mm[1], 10);
  return h >= 4 && h <= 60 ? h : null;
};

// ── rigs (side view, 460x250 viewBox, ground at y=232) ─────────────────────
const BoomRig = ({ p }) => {
  const boomDeg = p.boom ?? 0;
  const jibDeg = p.jib ?? 0;
  const extendM = p.extend ?? 0;
  const tiltDeg = p.tiltX ?? 0;
  const overload = p.load != null && p.rated != null && p.load > p.rated;

  const P = { x: 158, y: 184 };
  const L = 128 + 88 * Math.min(1, extendM / 12);
  const rad = (boomDeg * Math.PI) / 180;
  const tip = { x: P.x + L * Math.cos(rad), y: P.y - L * Math.sin(rad) };
  const hasJib = p.jib != null;
  const jibLen = 46;
  const jibRad = (jibDeg * Math.PI) / 180;
  const cageAt = hasJib
    ? { x: tip.x + jibLen * Math.cos(jibRad), y: tip.y - jibLen * Math.sin(jibRad) }
    : tip;
  const cageFill = overload ? '#ef4444' : '#2563eb';

  return (
    <svg viewBox="0 0 460 250" style={{ width: '100%', height: '100%', display: 'block' }} role="img"
      aria-label={`Boom lift position: boom ${boomDeg.toFixed(1)} degrees`}>
      <line x1="8" y1="232" x2="452" y2="232" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="7 5" />
      <g transform={`rotate(${-tiltDeg}, 180, 232)`}>
        {/* 1. BASE */}
        <rect x="72" y="194" width="220" height="22" rx="6" fill="#475569" />
        <rect x="70" y="182" width="52" height="16" rx="3" fill="#334155" />
        <circle cx="112" cy="220" r="15" fill="#1e293b" /><circle cx="112" cy="220" r="6" fill="#94a3b8" />
        <circle cx="252" cy="220" r="15" fill="#1e293b" /><circle cx="252" cy="220" r="6" fill="#94a3b8" />
        <rect x="134" y="176" width="50" height="18" rx="4" fill="#64748b" />
        {/* 2. MAIN BOOM */}
        <g transform={`rotate(${-boomDeg}, ${P.x}, ${P.y})`}>
          <rect x={P.x - 6} y={P.y - 8} width={L * 0.62} height="15" rx="4" fill="#f59e0b" />
          <rect x={P.x - 6 + L * 0.5} y={P.y - 6} width={L * 0.5 + 6} height="11" rx="4" fill="#fbbf24" />
        </g>
        <circle cx={P.x} cy={P.y} r="6.5" fill="#0f172a" />
        {/* 3. JIB */}
        {hasJib && (
          <>
            <g transform={`translate(${tip.x}, ${tip.y}) rotate(${-jibDeg})`}>
              <rect x="-2" y="-4.5" width={jibLen + 4} height="9" rx="3.5" fill="#d97706" />
            </g>
            <circle cx={tip.x} cy={tip.y} r="4.5" fill="#0f172a" />
          </>
        )}
        {/* 4. CAGE */}
        <g transform={`translate(${cageAt.x}, ${cageAt.y})`}>
          <rect x="2" y="-10" width="30" height="20" rx="3" fill="none" stroke={cageFill} strokeWidth="3.2" />
          <line x1="2" y1="10" x2="32" y2="10" stroke={cageFill} strokeWidth="4" />
          <circle cx="17" cy="-15" r="3.6" fill={cageFill} />
        </g>
      </g>
    </svg>
  );
};

const ScissorRig = ({ p, maxHeightM }) => {
  const tiltDeg = p.tiltX ?? 0;
  const overload = p.loadPct != null && p.loadPct >= 100;
  // Pose from a real height signal when one exists; these models don't stream
  // one today, so the stack draws mid-rise as a schematic (never a fake reading).
  const rise = p.heightM != null && maxHeightM ? Math.min(1, p.heightM / maxHeightM) : 0.45;
  const stackH = 40 + 110 * rise;          // px from chassis top to platform
  const baseTop = 196;
  const platY = baseTop - stackH;
  const cx = 205;                           // stack centre
  const armW = 150;
  const pairs = 3;
  const platFill = overload ? '#ef4444' : '#2563eb';

  // 3 X-pairs sharing the stack height.
  const seg = stackH / pairs;
  const arms = [];
  for (let i = 0; i < pairs; i++) {
    const y1 = baseTop - i * seg;
    const y2 = baseTop - (i + 1) * seg;
    arms.push(
      <g key={i}>
        <line x1={cx - armW / 2} y1={y1} x2={cx + armW / 2} y2={y2} stroke="#f59e0b" strokeWidth="7" strokeLinecap="round" />
        <line x1={cx + armW / 2} y1={y1} x2={cx - armW / 2} y2={y2} stroke="#d97706" strokeWidth="7" strokeLinecap="round" />
        <circle cx={cx} cy={(y1 + y2) / 2} r="4" fill="#0f172a" />
      </g>
    );
  }

  return (
    <svg viewBox="0 0 460 250" style={{ width: '100%', height: '100%', display: 'block' }} role="img"
      aria-label={`Scissor lift position${maxHeightM ? `, working height up to ${maxHeightM} m` : ''}`}>
      <line x1="8" y1="232" x2="452" y2="232" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="7 5" />
      <g transform={`rotate(${-tiltDeg}, 205, 232)`}>
        {/* 1. BASE — chunky RT chassis */}
        <rect x="108" y="196" width="194" height="22" rx="6" fill="#475569" />
        <circle cx="140" cy="221" r="14" fill="#1e293b" /><circle cx="140" cy="221" r="5.5" fill="#94a3b8" />
        <circle cx="270" cy="221" r="14" fill="#1e293b" /><circle cx="270" cy="221" r="5.5" fill="#94a3b8" />
        {/* 2. SCISSOR STACK */}
        {arms}
        {/* 3. PLATFORM with rails + operator */}
        <g transform={`translate(0, ${platY})`}>
          <rect x={cx - armW / 2 - 14} y="-6" width={armW + 28} height="8" rx="3" fill={platFill} />
          <line x1={cx - armW / 2 - 12} y1="-6" x2={cx - armW / 2 - 12} y2="-26" stroke={platFill} strokeWidth="3.4" />
          <line x1={cx + armW / 2 + 12} y1="-6" x2={cx + armW / 2 + 12} y2="-26" stroke={platFill} strokeWidth="3.4" />
          <line x1={cx - armW / 2 - 12} y1="-26" x2={cx + armW / 2 + 12} y2="-26" stroke={platFill} strokeWidth="3.4" />
          <circle cx={cx} cy="-33" r="3.6" fill={platFill} />
        </g>
      </g>
      {/* model working-height dimension at the right */}
      {maxHeightM && (
        <g stroke="#94a3b8" strokeWidth="1.6">
          <line x1="392" y1="232" x2="392" y2="58" strokeDasharray="4 4" />
          <line x1="386" y1="232" x2="398" y2="232" />
          <line x1="386" y1="58" x2="398" y2="58" />
          <text x="400" y="140" fill="#64748b" fontSize="13" fontWeight="700" stroke="none">≤{maxHeightM} m</text>
        </g>
      )}
    </svg>
  );
};

const SpiderRig = ({ p }) => {
  const tiltDeg = p.tiltX ?? 0;
  const hasPose = p.boom != null;
  const lowerDeg = hasPose ? Math.min(75, p.boom + 18) : 30;   // folded-ish when parked
  const upperDeg = hasPose ? p.boom : -12;
  const P = { x: 208, y: 190 };
  const L1 = 82, L2 = 96;
  const r1 = (lowerDeg * Math.PI) / 180;
  const elbow = { x: P.x + L1 * Math.cos(r1), y: P.y - L1 * Math.sin(r1) };
  const r2 = (upperDeg * Math.PI) / 180;
  const tip = { x: elbow.x + L2 * Math.cos(r2), y: elbow.y - L2 * Math.sin(r2) };

  return (
    <svg viewBox="0 0 460 250" style={{ width: '100%', height: '100%', display: 'block' }} role="img"
      aria-label="Spider lift position">
      <line x1="8" y1="232" x2="452" y2="232" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="7 5" />
      <g transform={`rotate(${-tiltDeg}, 208, 232)`}>
        {/* 1. BASE — crawler tracks + compact body */}
        <rect x="150" y="210" width="120" height="20" rx="10" fill="#1e293b" />
        {[164, 186, 208, 230, 252].map(x => <circle key={x} cx={x} cy="220" r="6" fill="#94a3b8" />)}
        <rect x="162" y="186" width="94" height="26" rx="5" fill="#475569" />
        {/* 2/3. TWIN FOLDING BOOM */}
        <g transform={`rotate(${-lowerDeg}, ${P.x}, ${P.y})`}>
          <rect x={P.x - 5} y={P.y - 6.5} width={L1 + 5} height="12" rx="4" fill="#f59e0b" />
        </g>
        <circle cx={P.x} cy={P.y} r="5.5" fill="#0f172a" />
        <g transform={`translate(${elbow.x}, ${elbow.y}) rotate(${-upperDeg})`}>
          <rect x="-3" y="-5" width={L2 + 3} height="10" rx="4" fill="#fbbf24" />
        </g>
        <circle cx={elbow.x} cy={elbow.y} r="4.5" fill="#0f172a" />
        {/* 4. CAGE */}
        <g transform={`translate(${tip.x}, ${tip.y})`}>
          <rect x="0" y="-9" width="26" height="18" rx="3" fill="none" stroke="#2563eb" strokeWidth="3" />
          <line x1="0" y1="9" x2="26" y2="9" stroke="#2563eb" strokeWidth="3.6" />
          <circle cx="13" cy="-13.5" r="3.3" fill="#2563eb" />
        </g>
      </g>
    </svg>
  );
};

// ── chips ───────────────────────────────────────────────────────────────────
const StatChip = ({ label, value, unit, warn }) => (
  <Chip
    size="small"
    label={`${label}: ${value == null ? '—' : `${value}${unit || ''}`}`}
    sx={{
      fontWeight: 700, fontSize: '11px', borderRadius: '8px', justifyContent: 'flex-start',
      bgcolor: warn ? '#fee2e2' : value == null ? 'grey.100' : 'rgba(37,99,235,0.08)',
      color: warn ? '#b91c1c' : value == null ? 'text.disabled' : 'text.primary',
    }}
  />
);

// `embedded` renders the drawing ALONE — no Card chrome, no header, no stat
// chips — for hosting inside another card (the Live Data panel puts the rig on
// the left and the sensor readings on the right, so the chips would just repeat
// the list beside them). The standalone card is unchanged for every other use.
const MachinePostureCard = ({ frame, modelNumber, category, embedded = false, height = 230 }) => {
  const p = useMemo(() => extractPosture(frame), [frame]);
  const rig = rigTypeFor(category, modelNumber);
  const maxHeightM = modelWorkingHeightM(modelNumber);
  const ts = frame?.timestamp ? formatToIST(frame.timestamp, 'dd MMM · HH:mm:ss') : null;

  const boomPosed = p.boom != null || p.jib != null || p.extend != null;
  const scissorLive = p.loadPct != null || p.rpm != null || p.batteryV != null || p.soc != null || p.heightM != null;
  const overload = rig === 'scissor'
    ? (p.loadPct != null && p.loadPct >= 100)
    : (p.load != null && p.rated != null && p.load > p.rated);

  const hint = !frame
    ? 'Select a frame in the Data Log (or click a chart point) to pose the machine.'
    : rig === 'scissor'
      ? (!scissorLive ? 'This frame carries no platform signals (vehicle off or GPS-only frame) — the machine is drawn parked.' : null)
      : (!boomPosed ? 'This frame carries no boom/platform signals (vehicle off or GPS-only frame) — the machine is drawn parked.' : null);

  const svgRig = rig === 'scissor' ? <ScissorRig p={p} maxHeightM={maxHeightM} />
    : rig === 'spider' ? <SpiderRig p={p} />
    : <BoomRig p={p} />;

  // The 3D machine, where one exists for this model. A WebGL failure or a
  // missing GLB calls back and the vector rig takes over for good.
  const [view, setView] = useState(readView);
  const [no3D, setNo3D] = useState(false);
  const can3D = has3DModel(rig, modelNumber) && supportsWebGL() && !no3D;
  const show3D = can3D && view === '3d';
  const fallbackTo2D = useCallback(() => setNo3D(true), []);
  const pickView = (v) => {
    if (!v) return;
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* private mode — the session default stands */ }
  };

  const viewToggle = can3D ? (
    <ToggleButtonGroup exclusive size="small" value={view} onChange={(_, v) => pickView(v)}>
      <ToggleButton value="3d" sx={{ py: 0.1, px: 1, fontSize: 10.5, fontWeight: 800, textTransform: 'none' }}>3D</ToggleButton>
      <ToggleButton value="2d" sx={{ py: 0.1, px: 1, fontSize: 10.5, fontWeight: 800, textTransform: 'none' }}>2D</ToggleButton>
    </ToggleButtonGroup>
  ) : null;

  const rigDrawing = show3D ? (
    <Suspense fallback={
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    }>
      <MachineModel3D posture={p} height={height} onFallback={fallbackTo2D} />
    </Suspense>
  ) : svgRig;

  if (embedded) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
          <RigIcon color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="caption" fontWeight={800} color="text.secondary" noWrap>
            Machine position{modelNumber ? ` — ${modelNumber}` : ''}
          </Typography>
          {overload && <Chip size="small" color="error" label="OVERLOAD" sx={{ fontWeight: 800, height: 18, fontSize: '10px' }} />}
          {viewToggle && <Box sx={{ ml: 'auto' }}>{viewToggle}</Box>}
        </Box>
        {/* The rig SVG is sized `height: 100%`, so its box needs a DEFINITE
            height — inside a flex column it resolved against an auto-height
            parent and collapsed to nothing, drawing an empty grey panel. Take
            the height as an explicit number of pixels and never from flex. */}
        <Box
          data-testid="machine-rig-panel"
          sx={{
            height,
            bgcolor: 'rgba(148,163,184,0.06)', borderRadius: '16px', p: 1,
          }}
        >
          {rigDrawing}
        </Box>
        {hint && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            {hint}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Card sx={{ borderRadius: '24px', p: 2, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <RigIcon color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={800}>
            Machine position{modelNumber ? ` — ${modelNumber}` : ''}
          </Typography>
          {overload && <Chip size="small" color="error" label="OVERLOAD" sx={{ fontWeight: 800, height: 20 }} />}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {frame ? (ts ? `Frame ${ts}` : 'Selected frame') : 'No frame selected'}
          </Typography>
          {viewToggle}
        </Box>
      </Box>

      {hint && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          {hint}
        </Typography>
      )}

      <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
        <Box sx={{ flex: '1 1 340px', minWidth: 280, height, bgcolor: 'rgba(148,163,184,0.06)', borderRadius: '16px', p: 1 }}>
          {rigDrawing}
        </Box>
        <Stack spacing={0.75} sx={{ flex: '0 1 190px', minWidth: 170, justifyContent: 'center' }}>
          {rig === 'scissor' ? (
            <>
              <StatChip label="Working height" value={p.heightM != null ? p.heightM.toFixed(1) : (maxHeightM ? `up to ${maxHeightM}` : null)} unit=" m" />
              <StatChip label="Platform load" warn={overload} value={p.loadPct?.toFixed(1)} unit=" %" />
              <StatChip label="Engine" value={p.rpm?.toFixed(0)} unit=" rpm" />
              <StatChip label={p.soc != null ? 'Battery SOC' : 'Battery'} value={p.soc != null ? p.soc.toFixed(0) : p.batteryV?.toFixed(1)} unit={p.soc != null ? ' %' : ' V'} />
              <StatChip label="Hours" value={p.hours?.toFixed(1)} unit=" h" />
              <StatChip label="Tilt X / Y"
                value={p.tiltX == null && p.tiltY == null ? null : `${p.tiltX?.toFixed(1) ?? '—'}° / ${p.tiltY?.toFixed(1) ?? '—'}`}
                unit="°" />
            </>
          ) : (
            <>
              <StatChip label="Boom angle" value={p.boom?.toFixed(1)} unit="°" />
              <StatChip label="Extension" value={p.extend?.toFixed(1)} unit=" m" />
              <StatChip label="Jib angle" value={p.jib?.toFixed(1)} unit="°" />
              <StatChip label="Cage swing" value={p.cageSwing?.toFixed(1)} unit="°" />
              <StatChip label="Tilt X / Y"
                value={p.tiltX == null && p.tiltY == null ? null : `${p.tiltX?.toFixed(1) ?? '—'}° / ${p.tiltY?.toFixed(1) ?? '—'}`}
                unit="°" />
              <Tooltip title={p.rated != null ? `Rated capacity ${p.rated} kg` : ''}>
                <span>
                  <StatChip label="Cage load" warn={overload}
                    value={p.load == null ? null : `${p.load.toFixed(0)}${p.rated != null ? ` / ${p.rated.toFixed(0)}` : ''}`}
                    unit=" kg" />
                </span>
              </Tooltip>
            </>
          )}
        </Stack>
      </Box>
    </Card>
  );
};

export default MachinePostureCard;
